import { useServerFn } from "@tanstack/react-start";
import { Send } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { subscribeNewsletter } from "@/lib/bookings.functions";
import { cn } from "@/lib/utils";

export function NewsletterForm({
  className,
  variant = "dark",
}: {
  className?: string;
  variant?: "dark" | "card";
}) {
  const subscribe = useServerFn(subscribeNewsletter);
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    try {
      const result = await subscribe({ data: { email } });
      setDone(true);
      toast.success(
        result.status === "already_subscribed"
          ? "You are already on the list"
          : "You're subscribed — welcome aboard",
      );
    } catch (error) {
      console.error(error);
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setPending(false);
    }
  };

  if (done) {
    return (
      <p className={cn("text-sm text-gold", className)}>
        Thank you — we&apos;ll send you our best offers, no spam.
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className={cn("flex flex-col gap-3 sm:flex-row", className)}>
      <label htmlFor={`newsletter-${variant}`} className="sr-only">
        Email address
      </label>
      <Input
        id={`newsletter-${variant}`}
        type="email"
        required
        maxLength={255}
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className={cn(
          "flex-1",
          variant === "dark" && "border-white/15 bg-white/5 text-ink-foreground placeholder:text-ink-foreground/40",
        )}
      />
      <Button type="submit" variant="gold" disabled={pending}>
        <Send className="size-4" />
        {pending ? "Subscribing…" : "Subscribe"}
      </Button>
    </form>
  );
}
